/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Lazy Loading Utilities
 *
 * Provides utilities for code splitting and lazy loading components
 * with proper error boundaries and loading states.
 */

import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { logger } from "@/lib/utils/logger";
import dynamic from "next/dynamic";
import type { ComponentType } from "react";
import React, { Suspense, lazy } from "react";

// ============================================================================
// TYPES
// ============================================================================

interface LazyLoadOptions {
  /** Custom loading component */
  loading?: ComponentType | (() => React.ReactElement);
  /** Disable SSR for this component */
  ssr?: boolean;
  /** Preload component on hover */
  preloadOnHover?: boolean;
  /** Component name for debugging */
  name?: string;
}

// ============================================================================
// LAZY LOADING UTILITIES
// ============================================================================

/**
 * Default loading component
 */
function DefaultLoading() {
  return (
    <div className="flex items-center justify-center p-6">
      <LoadingSpinner size="lg" />
    </div>
  );
}

/**
 * Create a lazy-loaded component with error boundary
 */
export function createLazyComponent<
  T extends ComponentType<Record<string, unknown>>,
>(
  importFn: () => Promise<{ default: T }>,
  options: LazyLoadOptions = {},
): ComponentType<React.ComponentProps<T>> {
  const {
    loading: Loading = DefaultLoading,
    ssr = true,
    name: _name,
  } = options;

  const LazyComponent = lazy(async () => {
    try {
      const componentModule = await importFn();
      return componentModule;
    } catch (error) {
      logger.error(
        `[LazyLoad] Failed to load component: ${_name ?? "unknown"}`,
        {
          error,
        },
      );
      throw error;
    }
  });

  // Wrap with Suspense
  const WrappedComponent = (props: React.ComponentProps<T>) => {
    if (!ssr && typeof window === "undefined") {
      return <Loading />;
    }

    return (
      <Suspense fallback={<Loading />}>
        {/* @ts-expect-error - LazyComponent props type inference is complex */}
        <LazyComponent {...props} />
      </Suspense>
    );
  };

  WrappedComponent.displayName = _name ?? "LazyComponent";

  return WrappedComponent;
}

/**
 * Create a Next.js dynamic import with better defaults
 */
export function createDynamicImport<
  T extends ComponentType<Record<string, unknown>>,
>(
  importFn: () => Promise<{ default: T }>,
  options: LazyLoadOptions = {},
): ComponentType<React.ComponentProps<T>> {
  const {
    loading: Loading = DefaultLoading,
    ssr = true,
    name: _name2,
  } = options;

  // Next.js dynamic expects a function that returns ReactNode, not ComponentType
  const loadingComponent =
    typeof Loading === "function" && Loading.prototype?.isReactComponent
      ? () => React.createElement(Loading, {})
      : Loading;

  return dynamic(importFn, {
    loading: loadingComponent as (() => React.ReactNode) | undefined,
    ssr,
  }) as ComponentType<React.ComponentProps<T>>;
}

/**
 * Preload a component module
 */
export function preloadComponent(
  importFn: () => Promise<{ default: ComponentType<Record<string, unknown>> }>,
): void {
  // Use link preload for better browser support
  if (typeof window !== "undefined") {
    importFn().catch((error) => {
      logger.error("[LazyLoad] Failed to preload component", { error });
    });
  }
}

/**
 * Create a hover preloader
 */
export function createHoverPreloader(
  importFn: () => Promise<{ default: ComponentType<Record<string, unknown>> }>,
): {
  onMouseEnter: () => void;
  onFocus: () => void;
} {
  let preloaded = false;

  const preload = () => {
    if (!preloaded) {
      preloaded = true;
      preloadComponent(importFn);
    }
  };

  return {
    onMouseEnter: preload,
    onFocus: preload,
  };
}

// ============================================================================
// ROUTE-BASED LAZY LOADING
// ============================================================================

/**
 * Lazy load a page component
 */
export function lazyLoadPage<T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
  pageName?: string,
): ComponentType<React.ComponentProps<T>> {
  return createDynamicImport(importFn, {
    ssr: false, // Pages can be client-only for better performance
    name: pageName,
    loading: () => (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    ),
  });
}

/**
 * Lazy load a feature component (modals, drawers, etc.)
 */
export function lazyLoadFeature<
  T extends ComponentType<Record<string, unknown>>,
>(
  importFn: () => Promise<{ default: T }>,
  featureName?: string,
): ComponentType<React.ComponentProps<T>> {
  return createDynamicImport(importFn, {
    ssr: false,
    name: featureName,
    loading: () => (
      <div className="flex items-center justify-center p-4">
        <LoadingSpinner size="md" />
      </div>
    ),
  });
}

/**
 * Lazy load a heavy component (charts, editors, etc.)
 */
export function lazyLoadHeavy<T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
  componentName?: string,
): ComponentType<React.ComponentProps<T>> {
  return createDynamicImport(importFn, {
    ssr: false,
    name: componentName,
    loading: () => (
      <div className="flex items-center justify-center p-6">
        <LoadingSpinner size="lg" />
      </div>
    ),
  });
}
